import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { primeAudio, playAlertBeep } from "@/lib/beep";

const POLL_MS = 20000;

// Polls inquiries while the dashboard is open (admin or sub-admin) and alerts
// on anything newer than the last one this browser/user has already seen.
export function useInquiryAlerts(user) {
  const [inquiries, setInquiries] = useState([]);
  const [loadingInq, setLoadingInq] = useState(true);
  const [soundArmed, setSoundArmed] = useState(false);

  const lastSeenRef = useRef(null);
  const initializedRef = useRef(false);
  const storageKey = user ? `eazy_last_seen_inquiry_${user.email}` : null;

  useEffect(() => {
    if (!storageKey) return;
    lastSeenRef.current = localStorage.getItem(storageKey);
    initializedRef.current = false;
  }, [storageKey]);

  const armSound = useCallback(() => {
    setSoundArmed(primeAudio());
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/admin/inquiries");
        if (cancelled) return;
        const items = data.items || [];
        setInquiries(items);

        if (items.length > 0) {
          const newest = items[0].created_at;

          if (!initializedRef.current) {
            initializedRef.current = true;
            if (!lastSeenRef.current) {
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          } else {
            const freshOnes = lastSeenRef.current
              ? items.filter((i) => i.created_at > lastSeenRef.current)
              : [];
            if (freshOnes.length > 0) {
              freshOnes.forEach((inq) => {
                toast.success(`New inquiry from ${inq.name}`, { description: inq.email });
              });
              playAlertBeep();
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          }
        }
      } catch {
        // silent — avoid spamming errors from background polling
      } finally {
        if (!cancelled) setLoadingInq(false);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user, storageKey]);

  return { inquiries, setInquiries, loadingInq, soundArmed, armSound };
}
